import React, { useState, useEffect, createContext, useCallback, useRef } from "react";
import useApi from "./api/api";
// import mockNotificationData from "./notificationfeed.json";
export const DataContext = createContext();

const DataProvider = ({ children }) => {
  const api = useApi(false);
  const schedulerApi = useApi(true);
  const [accounts, setAccounts] = useState([]);
  const [defaultAccounts, setDefaultAccounts] = useState([]);
  const [preProcessedCards, setPreProcessedCards] = useState([]);
  const [cards, setCards] = useState([]);
  const [identifiers, setIdentifiers] = useState([]);
  const [history, setHistory] = useState([]);
  const [scheduledTransfers, setScheduledTransfers] = useState([]);
  const [notificationFeed, setNotificationFeed] = useState([]);
  // Pending Request for Pay rows where this user is the requestee — backed by
  // GET /v1/embedded/request/pending. These rows are intentionally excluded
  // from transfer history (only money movements show there) and live on the
  // PaymentRequests screen + bell-badge surface instead.
  const [paymentRequests, setPaymentRequests] = useState([]);
  // Transfer UUIDs the user has optimistically acted on locally (approved or
  // declined) — used to suppress them from "pending" filters immediately
  // after the action POST before the backend's pub/sub event has propagated
  // through the FSM.
  const [locallyHandledRequestIds, setLocallyHandledRequestIds] = useState([]);
  const [p2pConfig, setP2pConfig] = useState([]);
  // Settled flags for the initial post-login fetches. Eligibility screens
  // (e.g. SendMoney) must distinguish "still loading" from "genuinely has no
  // accounts/cards", so these flip true on success OR error.
  const [accountsFetched, setAccountsFetched] = useState(false);
  const [cardsFetched, setCardsFetched] = useState(false);
  const [p2pConfigFetched, setP2pConfigFetched] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [fundingToken, setFundingToken] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [scheduledTransfersLoading, setScheduledTransfersLoading] =
    useState(false);
  const [defaultAccountsLoading, setDefaultAccountsLoading] = useState(false);
  const [scheduledAlert, setScheduledAlert] = useState(false);
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    limit: 15,
    total: 0,
    hasMore: true,
    loadingMore: false
  });
  const historyPaginationRef = useRef(historyPagination);
  
  useEffect(() => {
    historyPaginationRef.current = historyPagination;
  }, [historyPagination]);


  useEffect(() => {
    const getAccounts = async () => {
      try {
        const response = await api.get("account");
        setAccounts(response.data);
      } catch (error) {
        console.error("get accounts error", error);
      } finally {
        setAccountsFetched(true);
      }
    };
    const getCards = async () => {
      try {
        const response = await api.get("card");
        setPreProcessedCards(response.data);
      } catch (error) {
        console.error("get cards error", error);
      } finally {
        setCardsFetched(true);
      }
    };
    const getIdentifiers = async () => {
      try {
        const response = await api.get("identifier");
        setIdentifiers(response.data);
      } catch (error) {
        console.error("get identifiers error", error);
      } finally {
      }
    };
    const getP2pConfig = async () => {
      try {
        const response = await api.get("p2p-config");
        setP2pConfig(response.data);
      } catch (error) {
        console.error("get p2p config error", error);
      } finally {
        setP2pConfigFetched(true);
      }
    };
    if (loggedIn) {
      getCards();
      getAccounts();
      getIdentifiers();
      getP2pConfig();
    }
  }, [api, loggedIn]);

  const getDefaultAccounts = useCallback(async () => {
    if (accounts.length) {
      setDefaultAccountsLoading(true);
      try {
        const response = await Promise.all(
          accounts.map((account) => {
            const accountResponse = api.get(`account/${account.id}/default`);
            return accountResponse;
          })
        );
        setDefaultAccounts(
          response.map(({ data }, j) => {
            return [accounts[j], data];
          })
        );
      } catch (error) {
        console.error("get default accounts error", error);
      } finally {
        setDefaultAccountsLoading(false);
      }
    }
  }, [accounts, api]);

  const getHistory = useCallback(async (loadMore = false) => {
    const currentPagination = historyPaginationRef.current;
    
    if (loadMore && (!currentPagination.hasMore || currentPagination.loadingMore)) {
      return;
    }
    
    const currentPage = loadMore ? currentPagination.page + 1 : 1;
    const limit = 15;
    
    if (loadMore) {
      setHistoryPagination(prev => ({ ...prev, loadingMore: true }));
    } else {
      setHistoryLoading(true);
      // Reset pagination when doing initial load
      setHistoryPagination({
        page: 1,
        limit: 15,
        total: 0,
        hasMore: true,
        loadingMore: false
      });
    }
    
    try {
      const response = await api.get(`transfer/history?page=${currentPage}&limit=${limit}`);
      const { data, page, total } = response.data;
      
      if (loadMore) {
        setHistory(prev => [...prev, ...data]);
      } else {
        setHistory(data);
      }
      
      const hasMore = (page * limit) < total;
      setHistoryPagination({
        page,
        limit,
        total,
        hasMore,
        loadingMore: false
      });
    } catch (error) {
      if (!loadMore) {
        setHistory(null);
      }
      console.error("get history error", error);
      setHistoryPagination(prev => ({ ...prev, loadingMore: false }));
    } finally {
      if (!loadMore) {
        setHistoryLoading(false);
      }
    }
  }, [api]);

  const getPaymentRequests = useCallback(async () => {
    try {
      const response = await api.get("request/pending");
      setPaymentRequests(response.data?.data || []);
    } catch (error) {
      console.error("get payment requests error", error);
    }
  }, [api]);

  const getScheduledTransfers = useCallback(async () => {
    setScheduledTransfersLoading(true);
    try {
      const response = await schedulerApi.get("schedules");
      setScheduledTransfers(
        response.data.schedules.filter(({ is_active }) => is_active)
      );
    } catch (error) {
      console.error("get scheduled transfers error", error);
    } finally {
      setScheduledTransfersLoading(false);
    }
  }, [schedulerApi]);

  useEffect(() => {
    if (loggedIn) {
      getHistory();
      getPaymentRequests();
    }
  }, [getHistory, getPaymentRequests, loggedIn]);

  useEffect(() => {
    if (loggedIn) {
      getDefaultAccounts();
    }
  }, [getDefaultAccounts, loggedIn]);

  useEffect(() => {
    if (loggedIn && p2pConfig.scheduled_recurring_enabled) {
      getScheduledTransfers();
    }
  }, [getScheduledTransfers, loggedIn, p2pConfig]);

  const getNotificationsFeed = useCallback(async () => {
    try {
      setNotificationFeed([]);
      const response = await api.get("notification/feed");
      response.data.results
        // use this locally for dummy results
        // mockNotificationData.results
        .filter(({ verb }) => verb !== "transfer_received")
        .forEach((item) => {
          item.activities.forEach((each) => {
            // Request for Pay payment_requested notifications used to populate
            // paymentRequests here. That's now driven by the dedicated
            // /v1/embedded/request/pending endpoint, which always reflects
            // current FSM state. Other RfP verbs (request_declined,
            // request_expired) still flow through notificationFeed so the
            // requester sees them as activity events.
            setNotificationFeed((notificationFeed) => [
              ...notificationFeed,
              each.data,
            ]);
          });
        });
    } catch (error) {
      console.error("get notification feed error", error);
    }
  }, [api]);

  useEffect(() => {
    if (loggedIn) {
      getNotificationsFeed();
    }
  }, [getNotificationsFeed, loggedIn]);


  useEffect(() => {

    const createCardDisplayLabel = (card) => {
      const account = accounts.find((account) => account.card_uuids.includes(card.uuid));
      if (!account) {
        // we should never get here, but just in case
        return `Card Ending ${card.last_four}`;
      }
      let accountType = account.code === "CK" ? "Checking" : "Savings";
      return `Card Ending ${card.last_four} - ${accountType}`;
    };

    
    if (accounts.length === 0 || preProcessedCards.length === 0) {
      console.log("no accounts and cards, returning");
      return;
    }
    // Create display_label for each card
    preProcessedCards.forEach((card) => {
      card.display_label = createCardDisplayLabel(card);
    });
    // Sort cards by display_label, putting Checking before Savings
    preProcessedCards.sort((a, b) => {
      // Put Checking before Savings
      const aIsChecking = a.display_label.endsWith("- Checking");
      const bIsChecking = b.display_label.endsWith("- Checking");
      if (aIsChecking && !bIsChecking) return -1;
      if (!aIsChecking && bIsChecking) return 1;
      // If both are the same type, sort alphabetically by display_label
      return a.display_label.localeCompare(b.display_label);
    });
    setCards(preProcessedCards);
  }, [preProcessedCards, accounts]);
  
  useEffect(() => {

    if (accounts.length === 0 || cards.length === 0) {
      console.log("no accounts and cards, returning");
      return;
    }

    console.log("cards & accounts loaded");
  }, [cards, accounts]);


  // `cards` isn't populated directly by the fetch — the effect above merges
  // preProcessedCards with accounts to build display labels. So even after
  // both fetches settle there's one render where `cards` is still empty;
  // hold the loading state until that processing has landed too.
  const cardsProcessed =
    preProcessedCards.length === 0 || accounts.length === 0 || cards.length > 0;
  const initialDataLoading =
    !accountsFetched || !cardsFetched || !p2pConfigFetched || !cardsProcessed;

  return (
    <DataContext.Provider
      value={{
        loggedIn,
        setLoggedIn,

        initialDataLoading,
        

        history,
        historyLoading,
        historyPagination,
        getHistory,

        notificationFeed,
        getNotificationsFeed,
        paymentRequests,
        getPaymentRequests,
        locallyHandledRequestIds,
        markRequestLocallyHandled: (id) =>
          setLocallyHandledRequestIds((prev) =>
            prev.includes(id) ? prev : [...prev, id]
          ),

        scheduledTransfers,
        scheduledTransfersLoading,
        getScheduledTransfers,

        defaultAccounts,
        defaultAccountsLoading,
        getDefaultAccounts,

        accounts,

        cards,

        identifiers,

        p2pConfig,

        fundingToken,
        setFundingToken,

        scheduledAlert,
        setScheduledAlert,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export default DataProvider;
